import React, { useEffect, useState } from "react";
import ManualBadgeStore from "stores/ManualBadgeStore";
import FormControl, {
  DateFieldControl,
  TextFieldControl,
} from "components/FormControl";
import { ACCEPTED_IMAGE_TYPES } from "constants/image";
import {
  compose,
  fileSizeValidator,
  imageIsCorruptedValidator,
  imageTypesValidator,
} from "utils/validators";
import {
  Column,
  GroupRow,
  Row,
  Title,
  ImagePreviewContainer,
  UploadPreviewFieldControl,
  ControlsContainer,
  ControlContainer,
  LabelContainer,
} from "./styles";
import SimpleField from "components/SimpleField/SimpleField";
import SubmitButtonControl, {
  CancelButtonControl,
} from "components/FormControl/SubmitButtonControl";
import RestrictionStore from "stores/RestrictionStore";
import LoadingButton from "components/LoadingButton/LoadingButton";
import { Button, FormLabel } from "@material-ui/core";
import { FormSpy } from "react-final-form";
import Popup from "components/Popup/Popup";
import ConfirmationDialog from "components/ConfirmationDialog/ConfirmationDialog";
import { PUBLISH_STATUSES } from "constants/models";
import { formatDate } from "utils/date";

const MAX_FILE_SIZE_IN_MB = 10;

const initialFormValues = {
  id: -1,
  manualBadgeImage: "",
  internalName: "",
  displayDate: "",
  titleEnglish: "",
  popupCopyEnglish: "",
  titleSpanish: "",
  popupCopySpanish: "",
};

const ManualBadgeForm = ({
  isEditMode,
  isNewItem,
  badgeId,
  setIsEditMode,
  onBack,
  setPublishStatus,
  setSuccessfulUsers,
  setHasUsers,
}) => {
  const [initialValues, setInitialValues] = useState(initialFormValues);
  const [displayValues, setDisplayValues] = useState(initialFormValues);
  const [loading, setLoading] = useState(true);
  const [updateConfirmationIsOpened, setUpdateConfirmationIsOpened] = useState(
    false,
  );
  const [closeConfirmationIsOpened, setCloseConfirmationIsOpened] = useState(
    false,
  );
  const [isPopupDirty, setIsPopupDirty] = useState(false);
  const [status, setStatus] = useState("");

  const fetchInitialValues = async () => {
    if (isNewItem) {
      setInitialValues(initialFormValues);
      setLoading(false);
      return;
    }

    try {
      await ManualBadgeStore.fetchManualBadge(badgeId);
      const badgeData = ManualBadgeStore.manualBadge?.data;
      setPublishStatus(badgeData?.dataManualBadge.publishStatus);
      setStatus(badgeData?.dataManualBadge.publishStatus);
      setSuccessfulUsers(badgeData?.dataManualBadge.successfulUsersAmount);
      setHasUsers(badgeData?.dataManualBadge.successfulUsersAmount > 0);
      const formData = {
        id: badgeData?.id || 0,
        manualBadgeImage: badgeData?.dataManualBadge.fileUrl || "",
        internalName: badgeData?.dataManualBadge.name || "",
        displayDate: badgeData?.dataManualBadge.displayDate || "",
        titleEnglish: badgeData?.translations?.[0]?.title || "",
        popupCopyEnglish: badgeData?.translations?.[0]?.description || "",
        titleSpanish: badgeData?.translations?.[1]?.title || "",
        popupCopySpanish: badgeData?.translations?.[1]?.description || "",
      };
      setInitialValues(formData);
      setDisplayValues(formData);
    } catch (error) {
      console.error("Error fetching badge data:", error);
      setInitialValues(initialFormValues);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchInitialValues();
  }, [isEditMode, isNewItem, badgeId]);

  const renderCancelButton = ({ onClick }) => (
    <Button variant="outlined" color="primary" onClick={onClick} fullWidth>
      Cancel
    </Button>
  );

  const renderButton = ({ onClick }) => (
    <LoadingButton
      onClick={onClick}
      variant="contained"
      color="primary"
      isLoading={RestrictionStore.isManaging}
      fullWidth
    >
      {isNewItem ? "Add" : "Save"}
    </LoadingButton>
  );

  const onCancel = () => {
    if (isNewItem) {
      onBack();
    } else {
      if (isPopupDirty) {
        setCloseConfirmationIsOpened(true);
      } else {
        setIsEditMode(false);
      }
    }
  };

  const onChangeIsPopupDirty = ({ dirty }) => {
    setIsPopupDirty(dirty);
  };

  const closeUpdateConfirmation = () => {
    setUpdateConfirmationIsOpened(false);
  };

  const closeConfirmationPopup = () => {
    setCloseConfirmationIsOpened(false);
  };

  const openUpdateConfirmation = () => {
    setUpdateConfirmationIsOpened(true);
  };

  const confirmUpdate = values => () => {
    closeUpdateConfirmation();
    submit(values);
  };

  const closeConfirmation = () => {
    setInitialValues(displayValues);
    setIsEditMode(false);
    closeConfirmationPopup();
  };

  const onSubmit = () => {
    if (isPopupDirty) {
      openUpdateConfirmation();
    } else {
      setIsEditMode(false);
    }
  };

  const submit = async values => {
    const parsedValues = {
      dataManualBadge: {
        name: values.internalName,
        manualBadgeBlobFileId: values.manualBadgeImage.dataURL || "",
        displayDate: isNaN(Date.parse(values.displayDate))
          ? values.displayDate
          : new Date(values.displayDate).toISOString(),
      },
      ...(!isNewItem && { id: initialValues.id }),
      translations: [
        {
          localeId: "en-US",
          title: values.titleEnglish,
          description: values.popupCopyEnglish,
        },
        {
          localeId: "es-ES",
          title: values.titleSpanish,
          description: values.popupCopySpanish,
        },
      ],
    };

    try {
      if (isNewItem) {
        await ManualBadgeStore.createManualBadge(parsedValues);
        onBack();
      } else {
        await ManualBadgeStore.updateManualBadge(parsedValues);
        setIsEditMode(false);
        setIsPopupDirty(false);
        fetchInitialValues();
      }
    } catch (error) {
      console.error("Error submitting badge data:", error);
    }
  };

  const formFileValidator = acceptedTypes => value =>
    imageTypesValidator(value?.type ?? "", acceptedTypes);

  const renderEditableFields = () => (
    <GroupRow>
      <Column width="60%">
        <Row>
          <UploadPreviewFieldControl
            variant="outlined"
            margin="none"
            title="Badge Image"
            name="manualBadgeImage"
            defaultMessage="Drag an image file here or browse to upload"
            acceptedFiles={ACCEPTED_IMAGE_TYPES}
            validate={compose(
              formFileValidator(ACCEPTED_IMAGE_TYPES),
              fileSizeValidator(MAX_FILE_SIZE_IN_MB),
              imageIsCorruptedValidator,
            )}
            required
          />
        </Row>
        <Row>
          <TextFieldControl
            label="Title - English"
            name="titleEnglish"
            variant="outlined"
            margin="none"
            required
            maxLength={30}
          />
        </Row>
        <Row>
          <TextFieldControl
            label="Title - Spanish"
            name="titleSpanish"
            variant="outlined"
            margin="none"
            required
            maxLength={30}
          />
        </Row>
      </Column>
      <Column marginLeft="40px">
        <Row width="60%" marginTop="28px">
          <TextFieldControl
            variant="outlined"
            margin="none"
            name="internalName"
            label="Name - Internal Use"
            required
            maxLength={30}
          />
        </Row>
        <Row width="60%" marginTop="50px">
          <DateFieldControl
            name="displayDate"
            title="Display Date"
            inputVariant="outlined"
            margin="none"
            required
            fullWidth
          />
        </Row>
        <Row>
          <TextFieldControl
            label="Popup copy - English"
            name="popupCopyEnglish"
            variant="outlined"
            margin="none"
            required
            maxLength={100}
          />
        </Row>
        <Row>
          <TextFieldControl
            label="Popup copy - Spanish"
            name="popupCopySpanish"
            variant="outlined"
            margin="none"
            required
            maxLength={100}
          />
        </Row>
      </Column>
    </GroupRow>
  );

  const renderNonEditableFields = () => (
    <GroupRow>
      <Column width="60%">
        <Row>
          <LabelContainer>
            <FormLabel>Badge Image</FormLabel>
          </LabelContainer>
        </Row>
        <Row>
          <ImagePreviewContainer
            title=""
            image={displayValues.manualBadgeImage}
          />
        </Row>
      </Column>
      <Column marginLeft="40px">
        <Row width="40%">
          <SimpleField
            label="Name - Internal Use"
            value={displayValues.internalName}
          />
        </Row>
        <Row>
          <SimpleField
            label="Title - English"
            value={displayValues.titleEnglish}
          />
        </Row>
        <Row>
          <SimpleField
            label="Title - Spanish"
            value={displayValues.titleSpanish}
          />
        </Row>
      </Column>
      <Column marginLeft="40px">
        <Row width="40%">
          <SimpleField
            label="Display Date"
            value={formatDate(displayValues.displayDate)}
          />
        </Row>
        <Row>
          <SimpleField
            label="Popup copy - English"
            value={displayValues.popupCopyEnglish}
          />
        </Row>
        <Row>
          <SimpleField
            label="Popup copy - Spanish"
            value={displayValues.popupCopySpanish}
          />
        </Row>
      </Column>
    </GroupRow>
  );

  return (
    <>
      {closeConfirmationIsOpened && (
        <Popup onClose={closeConfirmationPopup} withoutExitButton>
          <ConfirmationDialog
            title="Discard Changes"
            text="Are you sure you would like to discard changes? All information will be lost."
            declineText="Cancel"
            acceptText="Discard"
            onDecline={closeConfirmationPopup}
            onAccept={closeConfirmation}
          />
        </Popup>
      )}
      <FormControl
        onSubmit={onSubmit}
        initialValues={initialValues}
        keepDirtyOnReinitialize={isNewItem}
      >
        {updateConfirmationIsOpened && (
          <FormSpy subscription={{ values: true }}>
            {({ values }) => (
              <Popup onClose={closeUpdateConfirmation} withoutExitButton>
                <ConfirmationDialog
                  title={
                    status === PUBLISH_STATUSES.PUBLISHED
                      ? "Save Changes to Production"
                      : "Save Changes"
                  }
                  text={
                    status === PUBLISH_STATUSES.PUBLISHED
                      ? "All changes made will be published to production. Are you sure you want to proceed?"
                      : "Are you sure you want to proceed?"
                  }
                  acceptText="Proceed"
                  declineText="Cancel"
                  onAccept={confirmUpdate(values)}
                  onDecline={closeUpdateConfirmation}
                />
              </Popup>
            )}
          </FormSpy>
        )}
        <FormSpy
          subscription={{ dirty: true }}
          onChange={onChangeIsPopupDirty}
        />
        {isEditMode && (
          <ControlsContainer>
            <ControlContainer>
              <CancelButtonControl
                render={renderCancelButton}
                onClick={onCancel}
              />
            </ControlContainer>
            <ControlContainer>
              <SubmitButtonControl render={renderButton} onClick={onSubmit} />
            </ControlContainer>
          </ControlsContainer>
        )}
        <Title>Badge</Title>
        {isEditMode ? renderEditableFields() : renderNonEditableFields()}
      </FormControl>
    </>
  );
};

export default ManualBadgeForm;
